import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { getFooter } from '../../lib/flotiq-client/external-services-requests';
import { checkResponseStatus } from '../../lib/flotiq-client/response-errors';
import { getFlotiqMainPageUrl } from '../../lib/helpers';

import {
  DiscordWhiteIcon,
  FacebookWhiteIcon,
  GithubWhiteIcon,
  LinkedinWhiteIcon,
  RedditWhiteIcon,
  TwitterWhiteIcon,
  YoutubeWhiteIcon,
} from '../../images/shapes';

import footerPlaceholderData from './placeholder-data/footer.json';

const icons = {
  Github: GithubWhiteIcon,
  Discord: DiscordWhiteIcon,
  YouTube: YoutubeWhiteIcon,
  Twitter: TwitterWhiteIcon,
  Facebook: FacebookWhiteIcon,
  LinkedIn: LinkedinWhiteIcon,
  Reddit: RedditWhiteIcon,
};

const hoverColors = {
  Github: 'hover:text-[#000000]',
  Discord: 'hover:text-[#7289DA]',
  YouTube: 'hover:text-[#FF0000]',
  Twitter: 'hover:text-[#3C406B]',
  Facebook: 'hover:text-[#4267B2]',
  LinkedIn: 'hover:text-[#0077B5]',
  Reddit: 'hover:text-[#EB5428]',
};

const QUERY_KEY = ['navigation_space', 'footer'];

export const useFooter = () => {
  const year = new Date().getFullYear();

  const {
    isLoading: requestLoading,
    data: response,
    isPlaceholderData,
  } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async ({ signal }) => {
      const result = await getFooter(undefined, undefined, undefined, {
        signal,
      });
      checkResponseStatus(result.body, result.status);
      return result;
    },
    enabled: true,
    staleTime: Infinity,
    gcTime: 0,
    retry: 0,
  });

  const isLoading = requestLoading || isPlaceholderData;

  const {
    footer_legal,
    github_url,
    discord_url,
    youtube_url,
    twitter_url,
    facebook_url,
    linkedin_url,
    reddit_url,
  } = useMemo(() => response?.body || footerPlaceholderData, [response?.body]);

  const legal = useMemo(
    () =>
      footer_legal?.map(({ text, url, new_tab }) => ({
        name: text,
        link: getFlotiqMainPageUrl(url),
        newTab: new_tab,
      })),
    [footer_legal],
  );

  const socials = useMemo(
    () =>
      [
        {
          name: 'Github',
          link: github_url,
        },
        {
          name: 'Discord',
          link: discord_url,
        },
        {
          name: 'YouTube',
          link: youtube_url,
        },
        {
          name: 'Twitter',
          link: twitter_url,
        },
        {
          name: 'Facebook',
          link: facebook_url,
        },
        {
          name: 'LinkedIn',
          link: linkedin_url,
        },
        {
          name: 'Reddit',
          link: reddit_url,
        },
      ]
        .filter(({ link }) => link)
        .map(({ name, link }) => {
          const IconComponent = icons[name];
          return {
            name,
            link,
            icon: (
              <IconComponent
                className={`transition duration-200 ease-in-out h-7 w-auto text-slate-400/40 ${hoverColors[name]}`}
              />
            ),
          };
        }),
    [
      discord_url,
      facebook_url,
      github_url,
      linkedin_url,
      reddit_url,
      twitter_url,
      youtube_url,
    ],
  );

  return {
    isLoading: isLoading,
    footer: {
      copyright: `@ ${year} Flotiq`,
      linksList: legal,
      socials,
    },
  };
};
